import { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd'
import { PlusOutlined, SyncOutlined, ThunderboltOutlined } from '@ant-design/icons'
import { adminApi } from '../../api'
import { KNOWLEDGE_SCOPE_OPTIONS_SHORT } from '../../constants/knowledgeScope'
import { PageHeader, tablePagination, useServerPagination } from '../../components/ui'

type Synonym = { id: number; word: string; synonyms: string; is_active: boolean }
type CustomTerm = {
  id: number
  term: string
  aliases: string
  remark: string
  is_active: boolean
}
type Candidate = {
  id: number
  kind: string
  primary_text: string
  secondary_text: string
  source: string
  status: string
  confidence: number | null
  remark: string
  evidence?: Record<string, unknown>
}
type QueryInsight = {
  query: string
  scope: string
  count: number
  max_score: number | null
}

const SOURCE_LABELS: Record<string, string> = {
  kb_meta: '知识库元数据',
  llm: 'LLM 抽取',
  csv: 'CSV 导入',
  query_log: '检索日志',
  embedding: '向量聚类',
}

function showEsSyncWarning(data?: { es_sync?: string; es_sync_message?: string }) {
  if (data?.es_sync === 'failed') {
    message.warning(data.es_sync_message || 'ES 增量同步失败，请稍后点击「全量对账 ES」')
  }
}

export default function SearchDict() {
  const [syncing, setSyncing] = useState(false)
  const [dictStats, setDictStats] = useState({ synonyms: 0, custom_terms: 0 })
  const [mining, setMining] = useState(false)

  const [synonyms, setSynonyms] = useState<Synonym[]>([])
  const synPag = useServerPagination()
  const termPag = useServerPagination()
  const candPag = useServerPagination()
  const [synTotal, setSynTotal] = useState(0)
  const [synOpen, setSynOpen] = useState(false)
  const [synEd, setSynEd] = useState<Synonym | null>(null)
  const [synForm] = Form.useForm()

  const [terms, setTerms] = useState<CustomTerm[]>([])
  const [termTotal, setTermTotal] = useState(0)
  const [termOpen, setTermOpen] = useState(false)
  const [termEd, setTermEd] = useState<CustomTerm | null>(null)
  const [termForm] = Form.useForm()

  const [candidates, setCandidates] = useState<Candidate[]>([])
  const [candTotal, setCandTotal] = useState(0)
  const [selectedCand, setSelectedCand] = useState<number[]>([])
  const [candOpen, setCandOpen] = useState(false)
  const [candEd, setCandEd] = useState<Candidate | null>(null)
  const [candForm] = Form.useForm()
  const [approvingAll, setApprovingAll] = useState(false)
  const [rejectingAll, setRejectingAll] = useState(false)

  const [insights, setInsights] = useState<QueryInsight[]>([])

  const [mineScope, setMineScope] = useState('enterprise')

  const loadDictStats = useCallback(() => {
    adminApi.get<{ synonyms: number; custom_terms: number }>('/admin/search-dict/stats').then((r) => {
      setDictStats(r.data)
    })
  }, [])

  const loadSynonyms = useCallback(
    (p = synPag.page, ps = synPag.pageSize) => {
      adminApi
        .get<{ items: Synonym[]; total: number }>('/admin/search-dict/synonyms', {
          params: { page: p, page_size: ps },
        })
        .then((r) => {
          setSynonyms(r.data.items)
          setSynTotal(r.data.total)
        })
    },
    [synPag.page, synPag.pageSize],
  )

  const loadTerms = useCallback(
    (p = termPag.page, ps = termPag.pageSize) => {
      adminApi
        .get<{ items: CustomTerm[]; total: number }>('/admin/search-dict/custom-terms', {
          params: { page: p, page_size: ps },
        })
        .then((r) => {
          setTerms(r.data.items)
          setTermTotal(r.data.total)
        })
    },
    [termPag.page, termPag.pageSize],
  )

  const loadCandidates = useCallback(
    (p = candPag.page, ps = candPag.pageSize) => {
      adminApi
        .get<{ items: Candidate[]; total: number }>('/admin/search-dict/candidates', {
          params: { page: p, page_size: ps, status: 'pending' },
        })
        .then((r) => {
          setCandidates(r.data.items)
          setCandTotal(r.data.total)
          setSelectedCand([])
        })
    },
    [candPag.page, candPag.pageSize],
  )

  const loadInsights = useCallback(() => {
    adminApi
      .get<{ items: QueryInsight[] }>('/admin/search-dict/query-insights', {
        params: { days: 30, max_hit: 0, min_count: 1, limit: 40 },
      })
      .then((r) => setInsights(r.data.items))
  }, [])

  useEffect(() => {
    loadDictStats()
    loadInsights()
  }, [loadDictStats, loadInsights])

  useEffect(() => {
    loadSynonyms()
  }, [loadSynonyms])

  useEffect(() => {
    loadTerms()
  }, [loadTerms])

  useEffect(() => {
    loadCandidates()
  }, [loadCandidates])

  const syncToEs = () => {
    setSyncing(true)
    adminApi
      .post<{ reloaded_indices: string[]; mode?: string }>('/admin/search-dict/sync')
      .then((r) => {
        message.success(
          `全量对账完成，重载索引：${r.data.reloaded_indices.join(', ') || '（无已存在索引）'}`,
        )
        loadDictStats()
      })
      .catch((e) => message.error(e.message))
      .finally(() => setSyncing(false))
  }

  const runMine = async (fn: () => Promise<{ data: Record<string, unknown> }>, okMsg: string) => {
    setMining(true)
    try {
      const r = await fn()
      const d = r.data as {
        items_scanned?: number
        candidates_created?: number
        hint?: string
        scope?: string
        status_breakdown?: { total_active?: number; indexed?: number; by_status?: object }
      }
      if (d.hint) {
        message.warning(d.hint, 8)
      }
      message.success(
        `${okMsg}：扫描 ${d.items_scanned ?? 0} 篇，新增候选 ${d.candidates_created ?? 0} 条` +
          (d.scope ? `（${d.scope}）` : ''),
      )
      candPag.resetPage()
      loadCandidates(1)
      loadDictStats()
    } catch (e) {
      message.error(e instanceof Error ? e.message : '执行失败')
    } finally {
      setMining(false)
    }
  }

  const afterApprove = (approved: number, esSyncFailed?: number, esSyncMessage?: string) => {
    message.success(`已通过 ${approved} 条`)
    if (esSyncFailed) {
      message.warning(esSyncMessage || '部分条目未能增量同步到 ES')
    }
    loadCandidates()
    loadSynonyms()
    loadTerms()
    loadDictStats()
  }

  const approveSelected = (ids: number[]) => {
    if (!ids.length) return
    adminApi
      .post<{ approved: number; es_sync_failed?: number; es_sync_message?: string }>(
        '/admin/search-dict/candidates/approve',
        { ids },
      )
      .then((r) => afterApprove(r.data.approved, r.data.es_sync_failed, r.data.es_sync_message))
  }

  const approveAllPending = () => {
    setApprovingAll(true)
    adminApi
      .post<{ approved: number; es_sync_failed?: number; es_sync_message?: string }>(
        '/admin/search-dict/candidates/approve-all',
      )
      .then((r) => afterApprove(r.data.approved, r.data.es_sync_failed, r.data.es_sync_message))
      .catch((e) => message.error(e instanceof Error ? e.message : '操作失败'))
      .finally(() => setApprovingAll(false))
  }

  const rejectAllPending = () => {
    setRejectingAll(true)
    adminApi
      .post<{ rejected: number }>('/admin/search-dict/candidates/reject-all')
      .then((r) => {
        message.success(`已拒绝 ${r.data.rejected} 条`)
        loadCandidates()
      })
      .catch((e) => message.error(e instanceof Error ? e.message : '操作失败'))
      .finally(() => setRejectingAll(false))
  }

  const openEditCandidate = (row: Candidate) => {
    setCandEd(row)
    candForm.setFieldsValue({
      kind: row.kind,
      primary_text: row.primary_text,
      secondary_text: row.secondary_text,
      remark: row.remark,
    })
    setCandOpen(true)
  }

  const csvTemplate = `kind,primary,secondary,remark
custom_term,电新,电力设备,行业简称
synonym,报销,费用报销,差旅报销,制度用语`

  return (
    <div>
      <PageHeader
        title="分词词库"
        description="维护 IK 分词用同义词与专有词；增删改后会增量同步到 ES。跨属性关联规则见「内容抽取 → 属性抽取」。"
      />
      <Space style={{ marginBottom: 16 }} wrap>
        <Button type="primary" icon={<SyncOutlined />} loading={syncing} onClick={syncToEs}>
          全量对账 ES
        </Button>
        <Select
          value={mineScope}
          style={{ width: 140 }}
          options={KNOWLEDGE_SCOPE_OPTIONS_SHORT}
          onChange={setMineScope}
        />
      </Space>
      <Tabs
        items={[
          {
            key: 'mine',
            label: '智能挖掘',
            children: (
              <Card>
                <Typography.Paragraph type="secondary">
                  使用 AI 从已索引知识库正文中抽取术语与同义词，生成「待审核」候选（不会直接写入 ES）。
                  审核通过后会增量同步；异常时可点「全量对账 ES」。请选对上方企业库/个人库。
                </Typography.Paragraph>
                <Space wrap style={{ marginBottom: 16 }}>
                  <Button
                    type="primary"
                    loading={mining}
                    icon={<ThunderboltOutlined />}
                    onClick={() =>
                      runMine(
                        () =>
                          adminApi.post('/admin/search-dict/mine/llm', {
                            scope: mineScope,
                            max_items: 30,
                            max_snippets_per_item: 1,
                          }),
                        'AI 抽取完成',
                      )
                    }
                  >
                    AI 抽术语与同义词
                  </Button>
                </Space>
                <Typography.Title level={5}>CSV 批量导入</Typography.Title>
                <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
                  表头：kind, primary, secondary, remark（kind 填 custom_term 或 synonym）
                </Typography.Paragraph>
                <Input.TextArea rows={4} defaultValue={csvTemplate} id="csv-import-area" />
                <Button
                  style={{ marginTop: 8 }}
                  loading={mining}
                  onClick={() => {
                    const el = document.getElementById('csv-import-area') as HTMLTextAreaElement
                    const content = el?.value?.trim()
                    if (!content) {
                      message.warning('请填写 CSV 内容')
                      return
                    }
                    runMine(
                      () => adminApi.post('/admin/search-dict/import/csv', { content }),
                      'CSV 导入完成',
                    )
                  }}
                >
                  导入为待审核
                </Button>
                <Upload
                  accept=".csv,.txt"
                  showUploadList={false}
                  beforeUpload={(file) => {
                    const reader = new FileReader()
                    reader.onload = () => {
                      const text = String(reader.result || '')
                      runMine(
                        () => adminApi.post('/admin/search-dict/import/csv', { content: text }),
                        '文件导入完成',
                      )
                    }
                    reader.readAsText(file)
                    return false
                  }}
                >
                  <Button style={{ marginLeft: 8 }}>或上传 CSV 文件</Button>
                </Upload>
              </Card>
            ),
          },
          {
            key: 'candidates',
            label: `待审核 (${candTotal})`,
            children: (
              <Card>
                <Space style={{ marginBottom: 12 }} wrap>
                  <Button
                    type="primary"
                    disabled={!selectedCand.length}
                    onClick={() => approveSelected(selectedCand)}
                  >
                    批量通过 ({selectedCand.length})
                  </Button>
                  <Popconfirm
                    title={`确认通过全部 ${candTotal} 条待审核候选？`}
                    description="将全部写入正式词库并同步 ES，不可批量撤销"
                    okText="全部通过"
                    disabled={candTotal <= 0}
                    onConfirm={approveAllPending}
                  >
                    <Button loading={approvingAll} disabled={candTotal <= 0}>
                      一键全部通过 ({candTotal})
                    </Button>
                  </Popconfirm>
                  <Popconfirm
                    title={`确认拒绝全部 ${candTotal} 条待审核候选？`}
                    description="将全部标记为已拒绝，不会写入词库"
                    okText="全部拒绝"
                    okButtonProps={{ danger: true }}
                    disabled={candTotal <= 0}
                    onConfirm={rejectAllPending}
                  >
                    <Button danger loading={rejectingAll} disabled={candTotal <= 0}>
                      一键全部拒绝 ({candTotal})
                    </Button>
                  </Popconfirm>
                </Space>
                <Table
                  rowKey="id"
                  size="small"
                  dataSource={candidates}
                  rowSelection={{
                    selectedRowKeys: selectedCand,
                    onChange: (keys) => setSelectedCand(keys as number[]),
                  }}
                  columns={[
                    {
                      title: '类型',
                      width: 88,
                      render: (_, r) =>
                        r.kind === 'synonym' ? <Tag color="purple">同义</Tag> : <Tag>专有</Tag>,
                    },
                    { title: '主词/专有词', dataIndex: 'primary_text', width: 140 },
                    { title: '别名/同义词', dataIndex: 'secondary_text', ellipsis: true },
                    {
                      title: '来源',
                      width: 100,
                      render: (_, r) => SOURCE_LABELS[r.source] || r.source,
                    },
                    {
                      title: '置信',
                      width: 56,
                      render: (_, r) =>
                        r.confidence != null ? (r.confidence * 100).toFixed(0) + '%' : '—',
                    },
                    { title: '备注', dataIndex: 'remark', ellipsis: true, width: 100 },
                    {
                      title: '操作',
                      width: 200,
                      render: (_, r) => (
                        <Space>
                          <Button size="small" type="link" onClick={() => openEditCandidate(r)}>
                            编辑
                          </Button>
                          <Button size="small" type="link" onClick={() => approveSelected([r.id])}>
                            通过
                          </Button>
                          <Popconfirm
                            title="拒绝该候选？"
                            onConfirm={() =>
                              adminApi
                                .post('/admin/search-dict/candidates/reject', { ids: [r.id] })
                                .then(() => {
                                  message.success('已拒绝')
                                  loadCandidates()
                                })
                            }
                          >
                            <Button size="small" type="link" danger>
                              拒绝
                            </Button>
                          </Popconfirm>
                        </Space>
                      ),
                    },
                  ]}
                  pagination={tablePagination(
                    candPag.page,
                    candTotal,
                    candPag.handlePaginationChange,
                    candPag.paginationOptions,
                  )}
                />
              </Card>
            ),
          },
          {
            key: 'insights',
            label: '检索洞察',
            children: (
              <Card>
                <Typography.Paragraph type="secondary">
                  统计近 30 天检索/对话中零命中的问句（需先有用户使用）。可据此补专有词或同义词。
                </Typography.Paragraph>
                <Table
                  rowKey={(r) => `${r.query}-${r.scope}`}
                  size="small"
                  dataSource={insights}
                  columns={[
                    { title: '问句', dataIndex: 'query', ellipsis: true },
                    { title: '库', dataIndex: 'scope', width: 100 },
                    { title: '次数', dataIndex: 'count', width: 64 },
                    {
                      title: '操作',
                      width: 100,
                      render: (_, r) => (
                        <Button
                          size="small"
                          onClick={() =>
                            adminApi
                              .post('/admin/search-dict/candidates/quick', { query: r.query })
                              .then((res) => {
                                message.success(res.data.created ? '已加入待审核' : '已存在或重复')
                                candPag.resetPage()
                                loadCandidates(1)
                              })
                              .catch((e) => message.error(e.message))
                          }
                        >
                          加候选
                        </Button>
                      ),
                    },
                  ]}
                  pagination={false}
                />
              </Card>
            ),
          },
          {
            key: 'synonyms',
            label: `同义词库 (${dictStats.synonyms})`,
            children: (
              <Card>
                <Space style={{ marginBottom: 12 }} wrap>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setSynEd(null)
                      synForm.resetFields()
                      synForm.setFieldsValue({ is_active: true })
                      setSynOpen(true)
                    }}
                  >
                    新增同义词组
                  </Button>
                  <Tag color="blue">主词 + 同义词（逗号分隔）</Tag>
                </Space>
                <Table
                  rowKey="id"
                  size="small"
                  dataSource={synonyms}
                  columns={[
                    { title: '主词', dataIndex: 'word', width: 140 },
                    { title: '同义词', dataIndex: 'synonyms', ellipsis: true },
                    {
                      title: '状态',
                      width: 72,
                      render: (_, r) =>
                        r.is_active ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>,
                    },
                    {
                      title: '操作',
                      width: 140,
                      render: (_, r) => (
                        <Space>
                          <Button
                            size="small"
                            onClick={() => {
                              setSynEd(r)
                              synForm.setFieldsValue(r)
                              setSynOpen(true)
                            }}
                          >
                            编辑
                          </Button>
                          <Popconfirm
                            title="确认删除？"
                            onConfirm={() =>
                              adminApi
                                .delete<{ es_sync?: string; es_sync_message?: string }>(
                                  `/admin/search-dict/synonyms/${r.id}`,
                                )
                                .then((res) => {
                                  message.success('已删除')
                                  showEsSyncWarning(res.data)
                                  loadSynonyms()
                                  loadDictStats()
                                })
                            }
                          >
                            <Button size="small" danger>
                              删
                            </Button>
                          </Popconfirm>
                        </Space>
                      ),
                    },
                  ]}
                  pagination={tablePagination(
                    synPag.page,
                    synTotal,
                    synPag.handlePaginationChange,
                    synPag.paginationOptions,
                  )}
                />
              </Card>
            ),
          },
          {
            key: 'terms',
            label: `专有词库 (${dictStats.custom_terms})`,
            children: (
              <Card>
                <Space style={{ marginBottom: 12 }} wrap>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setTermEd(null)
                      termForm.resetFields()
                      termForm.setFieldsValue({ is_active: true })
                      setTermOpen(true)
                    }}
                  >
                    新增专有词
                  </Button>
                </Space>
                <Table
                  rowKey="id"
                  size="small"
                  dataSource={terms}
                  columns={[
                    { title: '专有词', dataIndex: 'term', width: 160 },
                    { title: '别名', dataIndex: 'aliases', ellipsis: true },
                    { title: '备注', dataIndex: 'remark', ellipsis: true, width: 120 },
                    {
                      title: '状态',
                      width: 72,
                      render: (_, r) =>
                        r.is_active ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>,
                    },
                    {
                      title: '操作',
                      width: 140,
                      render: (_, r) => (
                        <Space>
                          <Button
                            size="small"
                            onClick={() => {
                              setTermEd(r)
                              termForm.setFieldsValue(r)
                              setTermOpen(true)
                            }}
                          >
                            编辑
                          </Button>
                          <Popconfirm
                            title="确认删除？"
                            onConfirm={() =>
                              adminApi
                                .delete<{ es_sync?: string; es_sync_message?: string }>(
                                  `/admin/search-dict/custom-terms/${r.id}`,
                                )
                                .then((res) => {
                                  message.success('已删除')
                                  showEsSyncWarning(res.data)
                                  loadTerms()
                                  loadDictStats()
                                })
                            }
                          >
                            <Button size="small" danger>
                              删
                            </Button>
                          </Popconfirm>
                        </Space>
                      ),
                    },
                  ]}
                  pagination={tablePagination(
                    termPag.page,
                    termTotal,
                    termPag.handlePaginationChange,
                    termPag.paginationOptions,
                  )}
                />
              </Card>
            ),
          },
        ]}
      />

      <Modal
        title={synEd ? '编辑同义词' : '新增同义词'}
        open={synOpen}
        onOk={() =>
          void synForm.validateFields().then((v) => {
            const req = synEd
              ? adminApi.put(`/admin/search-dict/synonyms/${synEd.id}`, v)
              : adminApi.post('/admin/search-dict/synonyms', v)
            return req.then((res) => {
              message.success('已保存')
              showEsSyncWarning(res.data)
              setSynOpen(false)
              loadSynonyms()
              loadDictStats()
            })
          })
        }
        onCancel={() => setSynOpen(false)}
        destroyOnHidden
      >
        <Form form={synForm} layout="vertical">
          <Form.Item name="word" label="主词" rules={[{ required: true }]}>
            <Input placeholder="如：报销" />
          </Form.Item>
          <Form.Item name="synonyms" label="同义词（逗号分隔）" rules={[{ required: true }]}>
            <Input placeholder="费用报销,差旅报销" />
          </Form.Item>
          <Form.Item name="is_active" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="编辑待审核候选"
        open={candOpen}
        onOk={() =>
          void candForm.validateFields().then((v) => {
            if (!candEd) return
            return adminApi
              .put(`/admin/search-dict/candidates/${candEd.id}`, v)
              .then(() => {
                message.success('已保存')
                setCandOpen(false)
                loadCandidates()
              })
          })
        }
        onCancel={() => setCandOpen(false)}
        destroyOnHidden
      >
        <Form form={candForm} layout="vertical">
          <Form.Item name="kind" label="类型" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'custom_term', label: '专有词' },
                { value: 'synonym', label: '同义词组' },
              ]}
            />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.kind !== cur.kind}
          >
            {({ getFieldValue }) => {
              const isSyn = getFieldValue('kind') === 'synonym'
              return (
                <>
                  <Form.Item
                    name="primary_text"
                    label={isSyn ? '主词' : '专有词'}
                    rules={[{ required: true }]}
                  >
                    <Input placeholder={isSyn ? '如：报销' : '如：电新'} />
                  </Form.Item>
                  <Form.Item
                    name="secondary_text"
                    label={isSyn ? '同义词（逗号分隔）' : '别名（逗号分隔，可选）'}
                    rules={isSyn ? [{ required: true, message: '同义词组至少填写一个同义词' }] : []}
                  >
                    <Input placeholder={isSyn ? '费用报销,差旅报销' : '电力设备,新能源'} />
                  </Form.Item>
                </>
              )
            }}
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={termEd ? '编辑专有词' : '新增专有词'}
        open={termOpen}
        onOk={() =>
          void termForm.validateFields().then((v) => {
            const req = termEd
              ? adminApi.put(`/admin/search-dict/custom-terms/${termEd.id}`, v)
              : adminApi.post('/admin/search-dict/custom-terms', v)
            return req.then((res) => {
              message.success('已保存')
              showEsSyncWarning(res.data)
              setTermOpen(false)
              loadTerms()
              loadDictStats()
            })
          })
        }
        onCancel={() => setTermOpen(false)}
        destroyOnHidden
      >
        <Form form={termForm} layout="vertical">
          <Form.Item name="term" label="专有词" rules={[{ required: true }]}>
            <Input placeholder="如：电新" />
          </Form.Item>
          <Form.Item name="aliases" label="别名（逗号分隔，可选）">
            <Input placeholder="电力设备,新能源" />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input />
          </Form.Item>
          <Form.Item name="is_active" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
